/**
 * Handles the UI for the batch processor.
 * 
 * It will add an event listener to the button with the
 * data-en-batch-processor-endpoint attribute, which will
 * contain the URL to the API endpoint.
 * 
 * @example
 *   <button class="en-batch-processor__button en-space-b-md"
 *      data-en-batch-processor-endpoint="<?php echo static::getEndpointUrl(static::ROUTE_DELETE_DRAFTED_PRODUCTIONS); ?>"
 *   >Label of the Action</button>
 * 
 * Calls an WP API endpoint to process the batch.
 * The API must return the following JSON data
 * which will be used to update the UI:
 * 
 * - toProcessCount ... Number;
 * - processedInCurrentIteration ... Number;
 * - hasMoreToProcess ... true | false;
 * - nextProcessIterationRequestData ... [paged: Number]
 * - currentProcessIterationRequestData ... [paged: Number]
 * - successCount ... Number
 * - warningCount ... Number
 * - errorCount ... Number
 * - log ... HTML String;
 * 
 * The process will loop through the API endpoint until the server tells
 * the process is done by returning hasMoreToProcess = false (empty)
 * 
 * @url /?action=batch_processor
 * @see BatchProcessorApiController.class.php
 * @author dominik@engarde.net
 */

var EN = EN || {};

EN.BatchProcessor = (function ($) {

	var selectors = {
		buttonTrigger: '[data-en-batch-processor-endpoint]',
		endpoint: '[data-en-batch-processor-endpoint]', // must be set on the button

		processStatus: '.en-batch-processor__process-status',
		toProcessTotalCount: '.en-batch-processor__to-process-total-count',
		toProcessCount: '.en-batch-processor__to-process-count', // left to process
		processedCount: '.en-batch-processor__processed-count',
		warningCount: '.en-batch-processor__warning-count',
		successCount: '.en-batch-processor__success-count',
		errorCount: '.en-batch-processor__error-count',

		logList: '.en-batch-processor__log-list',

		logFilterTrigger: '[data-en-batch-processor-filter-log-type]',
		logLine: '[data-en-batch-processor-log-type]',

	};

	var rawSelectors = {
		endpoint: 'data-en-batch-processor-endpoint',
		logFilterTriggerType: 'data-en-batch-processor-filter-log-type',
		logLineType: 'data-en-batch-processor-log-type',
		logListFilter: 'data-en-batch-processor-log-filtered-by',
		hidden: 'en-hidden',
		disabled: 'en-is-disabled',
	};

	var processStatusMessages = {
		'not-allowed': 'Sorry, action not allowed',
		'not-started': 'Not started',
		'starting': 'Starting',
		'running': 'Running, please don\'t close the browser tab during the process',
		'done': 'Done',
		'unknown-error': 'Unknown error, please check out the console',
		'connection-error': 'Failed: Seems like there is a connection error with the server',
	};

	var nonceKey = undefined; // nonce key

	var processIsRunning = false;
	var endpointUrl = undefined;

	var processStatus = 'not-started';
	var toProcessTotalCount = 0;
	var toProcessCount = 0; // how much is left
	var processedCount = 0;
	var warningCount = 0;
	var successCount = 0;
	var errorCount = 0;

	/**
	 * @param {JSON} data retrieved from the server. Expects following parameters:
	 * toProcessCount - int
	 * processedInCurrentIteration - int 
	 * hasMoreToProcess - bool
	 * log - string (HTML)
	 */
	var updateStatuses = function (data) {

		// update vars
		if (data) {
			if (toProcessTotalCount === 0) {
				toProcessTotalCount = data.toProcessCount;
			}
			toProcessCount = data.toProcessCount;
			processedCount += data.processedInCurrentIteration;
			warningCount += data.warningCount;
			errorCount += data.errorCount;
			successCount += data.successCount;
		}

		// update view
		$(selectors.processStatus).html(processStatusMessages[processStatus]);
		$(selectors.toProcessTotalCount).html(toProcessTotalCount);
		$(selectors.toProcessCount).html(toProcessCount);
		$(selectors.processedCount).html(processedCount);
		$(selectors.warningCount).html(warningCount);
		$(selectors.errorCount).html(errorCount);
		$(selectors.successCount).html(successCount);

		if (processIsRunning) {
			$(selectors.buttonTrigger).addClass(rawSelectors.disabled);
		} else {
			$(selectors.buttonTrigger).removeClass(rawSelectors.disabled);
		}
	}

	var updateLog = function (data) {

		// bail early
		if (!data) return;

		if (data.log) {
			$(selectors.logList).prepend(data.log);
		}
	}

	/**
	 * Sends request to the server. The server does the action and returns data, and sometimes also the data for 
	 * the next request.
	 * The function is recursive. As long as the server tells the process is not done it will continue looping.
	 */
	var sendProcessRequest = function (requestBody) {

		requestBody = requestBody || {};

		var fetchOptions = {}
		fetchOptions.method = 'POST';

		// headers
		fetchOptions.headers = {
			'Content-Type': 'application/json',
			'X-WP-Nonce': nonceKey,
		}

		if (requestBody) {
			fetchOptions.body = JSON.stringify(requestBody);
		}

		fetch(endpointUrl, fetchOptions)
			.then(function (res) {
				if (res.ok) {
					return res.json();
				}
				else {
					processStatus = 'connection-error';
					updateStatuses();
				}
			})
			.then(function (data) {
				if (data) {

					if (data.processStatus) {
						processStatus = data.processStatus;

						if (processStatus === 'not-allowed') {

							updateStatuses(data);
							updateLog(data);
							return;
						}
					}

					processStatus = 'running';
					updateStatuses(data);
					updateLog(data);

					// recursion until done
					if (data.hasMoreToProcess) {
						if (data.nextProcessIterationRequestData) {
							sendProcessRequest(data.nextProcessIterationRequestData);
						} else {
							sendProcessRequest();
						}
					} else {
						processStatus = 'done';
						processIsRunning = false;
						updateStatuses();
					}

				}
			})
			.catch(function (error) {
				processStatus = 'unknown-error';
				updateStatuses();

				console.log('Error: ', error);
			});
	}

	var startProcessing = function () {

		// reset 
		toProcessTotalCount = 0;
		toProcessCount = 0;
		processedCount = 0;
		warningCount = 0;
		successCount = 0;
		errorCount = 0;

		$(selectors.logList).empty();

		if (nonceKey) {
			processIsRunning = true;
			processStatus = 'starting';
		} else {
			processStatus = 'not-allowed';
		}

		updateStatuses();

		sendProcessRequest();

	}

	var listenStartButtonClick = function () {

		// processing
		$(selectors.endpoint).on('click', function () {

			var processName = $(this).text().trim();

			if (confirm('Are you sure you want to start the following process: \'' + processName + '\'?')) {

				endpointUrl = $(this).attr(rawSelectors.endpoint);

				// Bail if the process has already started
				if (processIsRunning) {
					console.log('The batch process is already running.');
					return;
				}

				startProcessing();

			}

		});

	}

	var listenLogFiltering = function () {

		$(selectors.logFilterTrigger).on('click', function () {
			var logType = $(this).attr(rawSelectors.logFilterTriggerType);
			$(selectors.logList).attr(rawSelectors.logListFilter, logType);
		});

	}

	/**
	 * Used to verify the user
	 */
	var setNonceKey = function () {
		if (EN.config) {
			nonceKey = EN.config.batchProcessorKey || undefined; // from backend
		}
	}

	var init = function () {

		// bail early
		if ($(selectors.endpoint).length === 0) return;

		setNonceKey();

		if (nonceKey) {
			listenStartButtonClick();
			listenLogFiltering();
		}

	}

	return {
		init: init,
	}

})(jQuery);